import selfPortrait from './assets/images/self-portrait.jpg';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import './App.css';

import utAustinLogo from './assets/images/ut-austin-logo.png';
import csumbLogo from './assets/images/csumb-logo.png';

function App() {
  return (
    <div className="App">
      <Navbar />

      <section id="about" className="portfolio-section">
        <h2>About</h2>
        <div className="about-content">
          <div className="profile-image-container">
            <img
              src={selfPortrait}
              alt="Juan Ansaldo"
              className="profile-image"
            />
          </div>
          <div className="about-text">
            <p>Computer science graduate and Master's student at UT Austin. I build full-stack and ML systems that solve real problems, from data pipelines and APIs to user-facing applications.</p>
            <p>My focus areas include machine learning, data engineering, and full-stack development. I work across the stack and enjoy shipping end-to-end products.</p>
          </div>
        </div>
      </section>

      <section id="education" className="portfolio-section">
        <h2>Education</h2>
        <ul className="education-list">
          <li className="education-item">
            <div className="education-logo-container">
              <img src={utAustinLogo} alt="University of Texas at Austin" className="education-logo" />
            </div>
            <div className="education-text">
              <h3>Master of Science in Computer Science</h3>
              <p className="education-meta">
                <strong>University of Texas at Austin</strong> · Expected May 2027
              </p>
            </div>
          </li>
          <li className="education-item">
            <div className="education-logo-container">
              <img src={csumbLogo} alt="California State University, Monterey Bay" className="education-logo" />
            </div>
            <div className="education-text">
              <h3>Bachelor of Science in Computer Science</h3>
              <p className="education-meta">
                <strong>California State University, Monterey Bay</strong> · May 2024
              </p>
            </div>
          </li>
        </ul>
      </section>

      <section id="experience" className="portfolio-section">
        <h2>Experience</h2>
        <ul className="experience-list">
          <li className="experience-item">
            <h3>Machine Learning Engineer Intern</h3>
            <p className="experience-meta">
              <strong>Naval Postgraduate School</strong> · Monterey, CA · July 2024 to March 2025
            </p>
            <ul className="experience-bullets">
              <li>Built scalable Python pipelines (parallel computing, multiprocessing) to process 40+ TB of NASA GPM satellite data for classification, regression, and latent heating estimation.</li>
              <li>Designed a modular PyTorch Lightning framework with configurable architectures, data augmentation, and Bayesian modeling to accelerate ML research.</li>
              <li>Reduced large-scale training time by 35% via Indexed WebDataset, FSDP, and custom optimizations.</li>
              <li>Collaborated with CS and atmospheric science researchers to interpret results and align deliverables with NASA objectives.</li>
              <li>Led experiments with ResNet50 and self-supervised learning on large-scale GPM datasets.</li>
            </ul>
          </li>
        </ul>
      </section>

      <section id="projects" className="portfolio-section">
        <h2>Projects</h2>
        <ul className="project-list">
          <li className="project-item">
            <h3>
              <a
                href="https://github.com/juanansaldo/self-supervised-vision-lab" 
                target="_blank"
                rel="noopener noreferrer"
                className="project-link"
              >
                Self-Supervised Vision Lab
              </a>
            </h3>
            <p className="project-desc">
              A comprehensive experiment framework for SimCLR self-supervised learning and supervised image classification with reproducible configs, automated training/testing, and scalable data preparation for CIFAR-10 and ImageNet.
            </p>
            <ul className="project-bullets">
              <li>Hydra-configured PyTorch Lightning pipeline that lets me swap models, datasets, and training settings with simple YAML overrides instead of changing code.</li>
              <li>Supports supervised classifiers (MNIST, CIFAR-10, ImageNet) and SimCLR self-supervised pretraining with linear probing evaluation, featuring consistent logging, checkpointing, and automated testing workflows.</li>
              <li>Includes bash shell scripts for ImageNet data preparation, WebDataset conversion, and launching experiment suites with organized output directories for logs, checkpoints, and metrics.</li>
            </ul>
          </li>
          <li className="project-item">
            <h3>
              <a
                href="https://github.com/juanansaldo/RAG-QA-app"
                target="_blank"
                rel="noopener noreferrer"
                className="project-link"
              >
                RAG Document Q&A
              </a>
            </h3>
            <p className="project-desc">
              I built this so I could run a full RAG pipeline locally: you upload your own PDFs or docs, and the app uses Ollama plus a vector store to answer questions using only that content. No cloud APIs required.
            </p>
            <ul className="project-bullets">
              <li>Backend is FastAPI with Chroma for embeddings and retrieval. You can tune how text is chunked and how many passages are pulled per question, and each session stays isolated so multiple users or demos don't mix.</li>
              <li>React (Vite) frontend: upload several files at once (they show up as tabs), ask questions in a chat-style flow, and expand any answer to see which chunks it came from. Ingest runs in the background so you can keep typing while files process.</li>
              <li>Accepts PDF, Word, CSV, and plain text. Sessions expire after inactivity so old data doesn't stick around forever.</li>
            </ul>
          </li>
          {/* <li className="project-item">
            <h3>
              <a
                href="https://www.las-michoacanas.com"
                target="_blank"
                rel="noopener noreferrer"
                className="project-link"
              >
                Las Michoacanas
              </a>
            </h3>
            <p className="project-desc">
              A site for a local ice cream shop with several locations. The owner needed one place to show menus and specials that could differ by store, and a simple way to update them without touching code.
            </p>
            <ul className="project-bullets">
              <li>React on the front, Node and MySQL on the back. The API serves the right menu for each location so when they add a new flavor or change hours, the site stays in sync without a separate build.</li>
              <li>Live on Heroku with their own domain and SSL. I added a small auth layer so only they can edit content; customers just browse and see what's available at each spot.</li>
            </ul>
          </li> */}
          <li className="project-item">
            <h3>
              <a
                href="https://youtu.be/8uMo0zHk3so"
                target="_blank"
                rel="noopener noreferrer"
                className="project-link"
              >
                FarmLens
              </a>
            </h3>
            <p className="project-desc">
              A HoloLens 2 app that helps farmers decide when to pick strawberries. You look at the plants through the headset and see ripeness labels over each berry so you can plan harvests instead of guessing.
            </p>
            <ul className="project-bullets">
              <li>Built in Unity with spatial mapping so the view lines up with the real field. The brain of it is a YOLOv9 model I fine-tuned in PyTorch on a strawberry image dataset; we got 85%+ accross various metrics (precision, recall, mAP) on ripeness (ripe, not ripe) in testing.</li>
              <li>Detection runs in real time and gets drawn onto the live view, so the farmer gets feedback right there instead of taking photos and checking later on a phone or laptop.</li>
            </ul>
          </li>
        </ul>
      </section>

      <Footer />
    </div>
  );
}

export default App;